/**
 * Maps the LLM's free-text color description (e.g. "bright yellow",
 * "reddish-brown", "colorless") to a renderable hex color.
 *
 * This IS the outcome-schema/rendering-vocabulary split from
 * chemistry-lab-target.md in miniature: the LLM stays free to describe any
 * color a real reaction produces, and the renderer has a bounded vocabulary
 * of primitives it can actually draw. A description this map doesn't
 * recognize falls back to a neutral tint rather than breaking — exactly the
 * "graceful degradation, not a hard constraint" rule the design argues for.
 * A color that hits the fallback repeatedly is a signal for what to add here
 * next, the same way an unauthored mix is a signal for the curated cache.
 */

// Three.js's Color accepts CSS color keywords directly, so single simple
// words ("blue", "yellow", "white") already work without any lookup — this
// table only needs the LLM's compound/descriptive phrases.
const PHRASE_COLORS: Record<string, string> = {
  // A touch more saturated than a bare white/cloudy fallback — a "colorless"
  // LIQUID (as opposed to a white SOLID/precipitate) still reads as clearly
  // there against the glass rather than looking like an empty vessel.
  colorless: '#cfe4f0',
  'colourless': '#cfe4f0',
  'bright yellow': '#ffd400',
  'golden yellow': '#ffc700',
  'light green': '#8fd68f',
  'dark green': '#2e7d32',
  'reddish-brown': '#8b4a2b',
  'reddish brown': '#8b4a2b',
  'silvery-white': '#e4e6e8',
  'silvery white': '#e4e6e8',
  'orange-yellow': '#ffb020',
  'orange yellow': '#ffb020',
  'cloudy white': '#eceff1',
  'white and cloudy': '#eceff1',
  'milky white': '#f1f3f5',
  'brick red': '#9c3a2b',
  // Cl2's classic description — without these, "pale yellow-green" hit the
  // plain /yellow/i fallback below (checked first) and lost the green
  // entirely, rendering as flat bright yellow instead.
  'pale yellow-green': '#d6df6e',
  'pale yellow green': '#d6df6e',
  'yellow-green': '#d6df6e',
  'yellow green': '#d6df6e',
  'yellowish-green': '#d6df6e',
  'yellowish green': '#d6df6e',
  'pale yellowish-green': '#d6df6e',
  'pale yellowish green': '#d6df6e',
  'greenish-yellow': '#d6df6e',
  'greenish yellow': '#d6df6e',
};

const KEYWORD_FALLBACKS: [pattern: RegExp, hex: string][] = [
  // Checked before either single colour below — a phrase mentioning BOTH
  // that isn't one of the exact phrases above (some other yellow-green
  // wording the LLM comes up with) would otherwise collapse to whichever
  // single-colour pattern happens to be tested first, silently dropping
  // the other hue.
  [/yellow.{0,12}green|green.{0,12}yellow/i, '#d6df6e'],
  [/yellow/i, '#ffd400'],
  [/green/i, '#4caf50'],
  [/blue/i, '#3b82c4'],
  [/red/i, '#c0392b'],
  [/brown/i, '#8b5a2b'],
  [/orange/i, '#ffa733'],
  [/purple|violet/i, '#8e5bc4'],
  [/pink/i, '#f2a3c0'],
  [/silver/i, '#c8ccd0'],
  [/black/i, '#3a3a3a'],
  [/(white|cloudy|colorless|colourless)/i, '#eef3f8'],
];

const DEFAULT_LIQUID_COLOR = '#cfe8f3';

/** Best-effort hex for a color description. Never throws — worst case is
 *  the neutral default, never a broken render. */
export function colorDescriptionToHex(description: string | undefined): string {
  if (!description) return DEFAULT_LIQUID_COLOR;
  const key = description.trim().toLowerCase();
  if (PHRASE_COLORS[key]) return PHRASE_COLORS[key];
  for (const [pattern, hex] of KEYWORD_FALLBACKS) {
    if (pattern.test(key)) return hex;
  }
  return DEFAULT_LIQUID_COLOR;
}

const HEX_PATTERN = /^#[0-9a-f]{6}$/i;

/**
 * Prefers a curated exact match in PHRASE_COLORS first, then the LLM's own
 * per-mix hex (`llmHex`, e.g. `color_change.to_hex`), then the keyword-regex
 * guess as a last resort.
 *
 * PHRASE_COLORS wins over `llmHex` deliberately, not the other way round:
 * "colorless" is asked for on nearly every mix, and a fresh per-call LLM hex
 * for it drifted warm/yellowish on some calls even though the text said
 * colorless — the whole point of hand-tuning `#cfe4f0` was a liquid that
 * reads as clearly-there-but-not-tinted, and trusting a new guess every time
 * throws that consistency away for no benefit. `llmHex` still does its job
 * for anything NOT in the curated set — a fresh compound description this
 * map has never seen — which is the long tail the static map can't keep up
 * with (see this file's header).
 */
export function resolveColorHex(llmHex: string | null | undefined, description: string | undefined): string {
  const key = description?.trim().toLowerCase();
  if (key && PHRASE_COLORS[key]) return PHRASE_COLORS[key];
  if (llmHex && HEX_PATTERN.test(llmHex.trim())) return llmHex.trim();
  return colorDescriptionToHex(description);
}
